import type { AnimationI, AnySceneObjectT, TweenT } from "tweenly"
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded"
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded"
import Box from "@mui/material/Box"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select, { SelectChangeEvent } from "@mui/material/Select"
import Tooltip from "@mui/material/Tooltip"
import { AnimationTweens, VideoPanelTweens } from "../../../../data/defaults/tweens.styles.defaults"
import { getAnimationCompatibility } from "../../../../helpers/animationCompatibility.helpers"
import ExpandableSection from "../../../common/ExpandableSection"
import CompactSelect from "../../../common/PropertyTools/CompactSelect"
import PropertyRow from "../../../common/PropertyTools/PropertyRow"
import AnimationStates from "./AnimationStates"

const TWEEN_TYPES = ["fromTo", "from", "to", "set"].map((type) => ({ value: type, label: type }))

interface Props {
    animation: AnimationI
    handleSetValue: (property: any, value: any) => void
    object?: AnySceneObjectT
}

const AnimationTween = ({ animation, handleSetValue, object }: Props) => {
    const handleSetTweenType = (e: { target: { value: string } }) => {
        const tween = {
            ...animation.tween,
            type: e.target.value,
        }
        handleSetValue("tween", tween)
    }

    const handleSetTween = (e: SelectChangeEvent) => {
        const value = e.target.value
        const animationObject = AnimationTweens.find((a) => a.name === value)

        if (animationObject) handleSetValue("tween", animationObject)
    }

    const animationTweens = object?.type === "video-panel" ? VideoPanelTweens : AnimationTweens

    return (
        <ExpandableSection
            expanded={true}
            title={"Animation"}
            component={
                <Box display="flex" flexDirection="column" gap={1.5} width="100%" pb={0.5}>
                    {/* tween and type side by side */}
                    <PropertyRow>
                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                            <Box minWidth={0}>
                                <FormControl fullWidth sx={{ m: 0 }}>
                                    <InputLabel shrink>tween</InputLabel>
                                    <Select
                                        name="animation"
                                        value={animation?.tween.name}
                                        onChange={handleSetTween}
                                        variant="filled"
                                        size="small"
                                        label="tween"
                                        fullWidth
                                        disableUnderline
                                        inputProps={{ "aria-label": "Tween" }}
                                        renderValue={(value) => {
                                            const compat = getAnimationCompatibility(
                                                object,
                                                value as string
                                            )
                                            return (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    {/* half a row: a long name is cut, the warning stays */}
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {value}
                                                    </Box>
                                                    {compat.level === "problematic" && (
                                                        <ErrorOutlineRoundedIcon
                                                            sx={{ fontSize: 16, flexShrink: 0 }}
                                                            color="error"
                                                        />
                                                    )}
                                                    {compat.level === "visual-difference" && (
                                                        <WarningAmberRoundedIcon
                                                            sx={{ fontSize: 16, flexShrink: 0 }}
                                                            color="warning"
                                                        />
                                                    )}
                                                </Box>
                                            )
                                        }}
                                    >
                                        {animationTweens.map((tween: TweenT, index: number) => {
                                            const compat = getAnimationCompatibility(
                                                object,
                                                tween.name
                                            )
                                            return (
                                                <MenuItem
                                                    key={index}
                                                    value={tween.name}
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                    }}
                                                >
                                                    {tween.name}
                                                    {compat.level !== "safe" && (
                                                        <Tooltip title={compat.message} arrow>
                                                            {compat.level === "problematic" ? (
                                                                <ErrorOutlineRoundedIcon
                                                                    sx={{ fontSize: 16, ml: 1 }}
                                                                    color="error"
                                                                />
                                                            ) : (
                                                                <WarningAmberRoundedIcon
                                                                    sx={{ fontSize: 16, ml: 1 }}
                                                                    color="warning"
                                                                />
                                                            )}
                                                        </Tooltip>
                                                    )}
                                                </MenuItem>
                                            )
                                        })}
                                    </Select>
                                </FormControl>
                            </Box>
                            {object && !["video-panel"].includes(object.type) && (
                                <Box minWidth={0}>
                                    <CompactSelect
                                        name="type"
                                        value={animation?.tween.type}
                                        options={TWEEN_TYPES}
                                        label="type"
                                        tooltip="Tween type"
                                        handleOnChange={handleSetTweenType}
                                    />
                                </Box>
                            )}
                        </Box>
                    </PropertyRow>
                    <AnimationStates animation={animation} type={animation?.tween.type} />
                </Box>
            }
        />
    )
}

export default AnimationTween
